import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import RealEstateBrokerDashboardAPI from 'dashboard/api/realEstateBrokerDashboard';

const DEFAULT_PAGE_SIZE = 20;
const STATUS_STORAGE_KEY = 'real_estate_broker_commission_status';
const FILTER_STORAGE_KEY = 'real_estate_broker_commission_filters';
const ORDER_STORAGE_KEY = 'real_estate_broker_commission_order';
const DEFAULT_STATUS = 'em_andamento';
const DEFAULT_FILTERS = { address_street: '', address_number: '' };
const DEFAULT_ORDER = { sort: 'data', direction: 'desc' };
const DEFAULT_STATE = {
  rows: [],
  recent: [],
  summary: [],
  addresses: [],
  meta: { count: 0, page: 1, per_page: DEFAULT_PAGE_SIZE, total_pages: 0 },
};

const readStorage = (storage, key, fallback) => {
  try {
    return JSON.parse(storage.getItem(key) || 'null') || fallback;
  } catch {
    return fallback;
  }
};

export function useRealEstateBrokerDashboard({ notify, t = key => key } = {}) {
  const status = ref(
    readStorage(window.sessionStorage, STATUS_STORAGE_KEY, DEFAULT_STATUS)
  );
  const filters = ref(
    readStorage(window.localStorage, FILTER_STORAGE_KEY, DEFAULT_FILTERS)
  );
  const order = ref(
    readStorage(window.localStorage, ORDER_STORAGE_KEY, DEFAULT_ORDER)
  );
  const page = ref(1);
  const state = ref(DEFAULT_STATE);
  const loading = ref(false);
  const error = ref('');
  const lastUpdatedAt = ref(null);
  const currentTime = ref(Date.now());
  let refreshTimer;

  const totalPages = computed(() => state.value.meta?.total_pages || 0);
  const totalRows = computed(() => state.value.meta?.count || 0);
  const minutesSinceRefresh = computed(() => {
    if (!lastUpdatedAt.value) return null;

    return Math.max(
      0,
      Math.floor((currentTime.value - lastUpdatedAt.value.getTime()) / 60000)
    );
  });

  const load = async () => {
    loading.value = true;
    error.value = '';
    try {
      const response = await RealEstateBrokerDashboardAPI.index({
        status: status.value,
        page: page.value,
        per_page: DEFAULT_PAGE_SIZE,
        recent_limit: 5,
        ...filters.value,
        ...order.value,
      });
      state.value = {
        ...DEFAULT_STATE,
        ...response.data,
        rows: response.data?.data || [],
      };
      lastUpdatedAt.value = new Date();
      currentTime.value = Date.now();
    } catch {
      error.value = t('REAL_ESTATE_BROKER_DASHBOARD.ERRORS.LOAD');
      if (notify) notify(error.value, null, { variant: 'error' });
    } finally {
      loading.value = false;
    }
  };

  const selectStatus = value => {
    status.value = value === 'total' ? DEFAULT_STATUS : value;
    page.value = 1;
    window.sessionStorage.setItem(
      STATUS_STORAGE_KEY,
      JSON.stringify(status.value)
    );
  };

  const applyAddress = value => {
    filters.value = { ...DEFAULT_FILTERS, ...value };
    page.value = 1;
    window.localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify(filters.value)
    );
  };

  const setOrder = value => {
    order.value = { ...DEFAULT_ORDER, ...value };
    page.value = 1;
    window.localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order.value));
  };

  const changePage = value => {
    page.value = Math.max(1, Math.min(value, totalPages.value || 1));
  };

  onMounted(() => {
    refreshTimer = window.setInterval(() => {
      currentTime.value = Date.now();
    }, 60000);
    load();
  });
  onBeforeUnmount(() => window.clearInterval(refreshTimer));
  watch([status, filters, order, page], load, { deep: true });

  return {
    status,
    filters,
    order,
    page,
    rows: computed(() => state.value.rows),
    recent: computed(() => state.value.recent),
    summary: computed(() => state.value.summary),
    addresses: computed(() => state.value.addresses),
    loading,
    error,
    totalPages,
    totalRows,
    minutesSinceRefresh,
    load,
    selectStatus,
    applyAddress,
    setOrder,
    changePage,
  };
}
